import Button from "@mui/joy/Button";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import FormLabel from "@mui/joy/FormLabel";
import Grid from "@mui/joy/Grid";
import Input from "@mui/joy/Input";
import Link from "@mui/joy/Link";
import Option from "@mui/joy/Option";
import Select from "@mui/joy/Select";
import Typography from "@mui/joy/Typography";
import { useEffect, useRef, useState } from "react";
import { LogRetentionDays } from "../../../configs/const";
import { PageLoading } from "../../../components/page_loading";
import { HostedZone } from "../../../services/backend_gateway/__generated__/backend_gateway-types";
import Checkbox from "@mui/joy/Checkbox";
import Switch from "@mui/joy/Switch";

export const EnvFeaturesComponent = (props: {
  loading: boolean;
  navigateToHelp: null | (() => void);
  deploymentId: string;
  envFeaturesParamsJson: string | null;
  envFeaturesStatus: string | null;
  hostedZones: HostedZone[];
  updateEnvFeaturesStack: (
    deploymentId: string,
    emailNotificationList: string,
    deploymentDomainName: string,
    logRetentionDays: number,
    restApiV1Enabled: boolean,
    restApiV1Subdomain: string,
    httpApiV2Enabled: boolean,
    httpApiV2Subdomain: string,
    vpcEnabled: boolean,
    vpcCidr: string,
    primaryPrivateCidr: string,
    secondaryPrivateCidr: string,
    primaryPublicCidr: string,
    secondaryPublicCidr: string,
    availabilityZone1: string,
    availabilityZone2: string,
    cognitoEnabled: boolean,
    cognitoAllowAdminCreateUserOnly: boolean,
    cognitoMfaRequired: boolean,
    cognitoAlertsEnabled: boolean,
    googleClientId: string,
    googleClientSecret: string,
    defaultAlarmsEnabled: boolean,
    defaultDatabaseRetention: number,
    defaultRequireManualCicdApproval: boolean,
    fromEmailName: string,
    callBack: null | (() => void)
  ) => void;
  myEmail: string;
}) => {
  const {
    loading,
    navigateToHelp,
    envFeaturesParamsJson,
    envFeaturesStatus,
    hostedZones,
    deploymentId,
    updateEnvFeaturesStack,
    myEmail,
  } = props;

  const [emailNotificationList, setEmailNotificationList] =
    useState<string>(myEmail);
  const [deploymentDomainName, setDeploymentDomainName] = useState<string>("");
  const [logRetentionDays, setLogRetentionDays] = useState<number>(365);
  const [restApiV1Enabled, setRestApiV1Enabled] = useState<boolean>(false);
  const [restApiV1Subdomain, setRestApiV1Subdomain] =
    useState<string>("restapi");
  const [httpApiV2Enabled, setHttpApiV2Enabled] = useState<boolean>(false);
  const [httpApiV2Subdomain, setHttpApiV2Subdomain] =
    useState<string>("httpapi");
  const [vpcEnabled, setVpcEnabled] = useState<boolean>(false);
  const [vpcCidr, setVpcCidr] = useState<string>("");
  const [primaryPrivateCidr, setPrimaryPrivateCidr] = useState<string>("");
  const [secondaryPrivateCidr, setSecondaryPrivateCidr] = useState<string>("");
  const [primaryPublicCidr, setPrimaryPublicCidr] = useState<string>("");
  const [secondaryPublicCidr, setSecondaryPublicCidr] = useState<string>("");
  const [availabilityZone1, setAvailabilityZone1] = useState<string>("a");
  const [availabilityZone2, setAvailabilityZone2] = useState<string>("b");

  const [cognitoEnabled, setCognitoEnabled] = useState<boolean>(false);
  const [cognitoAllowAdminCreateUserOnly, setCognitoAllowAdminCreateUserOnly] =
    useState<boolean>(false);
  const [cognitoAlertsEnabled, setCognitoAlertsEnabled] =
    useState<boolean>(false);
  const [cognitoMfaRequired, setCognitoMfaRequired] = useState<boolean>(false);
  const [googleClientId, setGoogleClientId] = useState<string>("");
  const [googleClientSecret, setGoogleClientSecret] = useState<string>("");
  const [defaultAlarmsEnabled, setDefaultAlarmsEnabled] =
    useState<boolean>(false);
  const [defaultDatabaseRetention, setDefaultDatabaseRetention] =
    useState<number>(30);
  const [
    defaultRequireManualCicdApproval,
    setDefaultRequireManualCicdApproval,
  ] = useState<boolean>(false);
  const [fromEmailName, setFromEmailName] = useState<string>("");

  const [error, setError] = useState<boolean>(false);
  const [showAdvancedSettings, setShowAdvancedSettings] =
    useState<boolean>(false);

  useEffect(() => {
    if (!envFeaturesParamsJson) return;
    try {
      const envFeaturesParams = JSON.parse(envFeaturesParamsJson);
      setDeploymentDomainName(
        envFeaturesParams.DeploymentDomainName
          ? `${envFeaturesParams.DeploymentDomainName}.`
          : ""
      );
      setEmailNotificationList(envFeaturesParams.EmailNotificationList || "");
      setLogRetentionDays(parseInt(envFeaturesParams.LogRetentionDays) || 365);
      setRestApiV1Enabled(envFeaturesParams.RestApiV1Enabled === "True");
      setRestApiV1Subdomain(envFeaturesParams.RestApiV1Subdomain || "");
      setHttpApiV2Enabled(envFeaturesParams.HttpApiV2Enabled === "True");
      setHttpApiV2Subdomain(envFeaturesParams.HttpApiV2Subdomain);
      setVpcEnabled(envFeaturesParams.VpcEnabled === "True");
      setVpcCidr(envFeaturesParams.VPCCidr);
      setPrimaryPrivateCidr(envFeaturesParams.PrimaryPrivateCidr);
      setSecondaryPrivateCidr(envFeaturesParams.SecondaryPrivateCidr);
      setPrimaryPublicCidr(envFeaturesParams.PrimaryPublicCidr);
      setSecondaryPublicCidr(envFeaturesParams.SecondaryPublicCidr);
      setAvailabilityZone1(envFeaturesParams.AvailabilityZone1);
      setAvailabilityZone2(envFeaturesParams.AvailabilityZone2);
      setCognitoEnabled(envFeaturesParams.CognitoEnabled === "True");
      setCognitoAllowAdminCreateUserOnly(
        envFeaturesParams.CognitoAllowAdminCreateUserOnly === "True"
      );
      setCognitoAlertsEnabled(
        envFeaturesParams.CognitoAlertsEnabled === "True"
      );
      setCognitoMfaRequired(envFeaturesParams.CognitoMfaRequired === "True");
      setGoogleClientId(envFeaturesParams.GoogleClientId);
      setGoogleClientSecret(envFeaturesParams.GoogleClientSecret);
      setDefaultAlarmsEnabled(
        envFeaturesParams.DefaultAlarmsEnabled === "True"
      );
      setDefaultDatabaseRetention(
        parseInt(envFeaturesParams.DefaultDatabaseRetention) || 30
      );
      setDefaultRequireManualCicdApproval(
        envFeaturesParams.DefaultRequireManualCicdApproval === "True"
      );
      setFromEmailName(envFeaturesParams.FromEmailName || "");
      setError(false);
    } catch (error) {
      setError(true);
      console.error("Error parsing envFeaturesParamsJson", error);
    }
  }, [envFeaturesParamsJson]);

  const logRetentionDaysInputRef = useRef<HTMLInputElement | null>(null);
  const validLogRetentionDays = LogRetentionDays.includes(logRetentionDays);

  return (
    <>
      <PageLoading loading={loading} />
      <Grid container spacing={2} sx={{ flexGrow: 1 }}>
        <Grid xs={12}>
          <Typography level="body-sm" sx={{ py: 1 }}>
            Waffle sets up the environment using AWS CloudFormation.{" "}
            <Link onClick={() => navigateToHelp && navigateToHelp()}>
              Read more
            </Link>
          </Typography>
        </Grid>
        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Select domain name</FormLabel>
            <Select value={deploymentDomainName}>
              <Option
                value={""}
                onClick={() => {
                  setDeploymentDomainName("");
                }}
              >
                AWS-generated domain- and hostnames
              </Option>
              {hostedZones.map((hostedZone) => (
                <Option
                  key={hostedZone.domainName}
                  value={hostedZone.domainName}
                  onClick={() => {
                    setDeploymentDomainName(hostedZone.domainName);
                  }}
                >
                  {hostedZone.domainName}
                </Option>
              ))}
            </Select>
            <FormHelperText>
              You can register custom domain names above.
            </FormHelperText>
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl>
            <FormLabel>Emails for alerts</FormLabel>
            <Input
              value={emailNotificationList}
              disabled={loading || error}
              onChange={(event) => {
                setEmailNotificationList(event.target.value);
              }}
            />
            <FormHelperText>
              Comma separated list of emails to send alerts to.
            </FormHelperText>
          </FormControl>
        </Grid>

        {envFeaturesParamsJson !== null && (
          <>
            <Grid xs={12}>
              <Checkbox
                checked={showAdvancedSettings}
                onChange={(event) => {
                  setShowAdvancedSettings(event.target.checked);
                }}
                label="Show advanced settings"
                size="sm"
                variant="outlined"
              />
            </Grid>
            {showAdvancedSettings && (
              <>
                <Grid xs={12} sm={restApiV1Enabled ? 6 : 12}>
                  <FormControl>
                    <FormLabel>Rest API V1</FormLabel>
                    <Typography
                      component="label"
                      startDecorator={
                        <Switch
                          checked={restApiV1Enabled}
                          onChange={(event) =>
                            setRestApiV1Enabled(event.target.checked)
                          }
                        />
                      }
                    >
                      {restApiV1Enabled ? "Enabled" : "Disabled"}
                    </Typography>
                    <FormHelperText>
                      The AWS ApiGateway V1 (REST) can be used for creating API
                      endpoints that require API Key or Cognito authentication.
                      It has a mandatory complex deployment process.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {restApiV1Enabled && (
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Rest API V1 subdomain</FormLabel>
                      <Input
                        value={restApiV1Subdomain}
                        disabled={loading || error}
                        onChange={(event) => {
                          setRestApiV1Subdomain(event.target.value);
                        }}
                      />
                      <FormHelperText>
                        The host name for the REST API V1:{" "}
                        {`https://${restApiV1Subdomain}.${deploymentDomainName}`}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                <Grid xs={12} sm={httpApiV2Enabled ? 6 : 12}>
                  <FormControl>
                    <FormLabel>HTTP API V2</FormLabel>
                    <Typography
                      component="label"
                      startDecorator={
                        <Switch
                          checked={httpApiV2Enabled}
                          onChange={(event) =>
                            setHttpApiV2Enabled(event.target.checked)
                          }
                        />
                      }
                    >
                      {httpApiV2Enabled ? "Enabled" : "Disabled"}
                    </Typography>
                    <FormHelperText>
                      The AWS ApiGateway V2 (HTTP) can be used for creating API
                      endpoints with Cognito authentication.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {httpApiV2Enabled && (
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>HTTP API V2 subdomain</FormLabel>
                      <Input
                        value={httpApiV2Subdomain}
                        disabled={loading || error}
                        onChange={(event) => {
                          setHttpApiV2Subdomain(event.target.value);
                        }}
                      />
                      <FormHelperText>
                        The host name for the HTTP API V2:{" "}
                        {`https://${httpApiV2Subdomain}.${deploymentDomainName}`}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                <Grid xs={12} sm={vpcEnabled ? 6 : 12}>
                  <FormControl>
                    <FormLabel>VPC with private subnets</FormLabel>
                    <Typography
                      component="label"
                      startDecorator={
                        <Switch
                          checked={vpcEnabled}
                          onChange={(event) =>
                            setVpcEnabled(event.target.checked)
                          }
                        />
                      }
                    >
                      {vpcEnabled ? "Enabled" : "Disabled"}
                    </Typography>
                    <FormHelperText>
                      Can be used for hosting backend services privately and
                      securely with strong access control.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {vpcEnabled && (
                  <>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>VPC CIDR</FormLabel>
                        <Input
                          value={vpcCidr}
                          disabled={loading || error}
                          onChange={(event) => {
                            setVpcCidr(event.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Primary private subnet CIDR</FormLabel>
                        <Input
                          value={primaryPrivateCidr}
                          disabled={loading || error}
                          onChange={(event) => {
                            setPrimaryPrivateCidr(event.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Secondary private subnet CIDR</FormLabel>
                        <Input
                          value={secondaryPrivateCidr}
                          disabled={loading || error}
                          onChange={(event) => {
                            setSecondaryPrivateCidr(event.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Primary public subnet CIDR</FormLabel>
                        <Input
                          value={primaryPublicCidr}
                          disabled={loading || error}
                          onChange={(event) => {
                            setPrimaryPublicCidr(event.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Secondary public subnet CIDR</FormLabel>
                        <Input
                          value={secondaryPublicCidr}
                          disabled={loading || error}
                          onChange={(event) => {
                            setSecondaryPublicCidr(event.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Availability zone 1</FormLabel>
                        <Input
                          value={availabilityZone1}
                          disabled={loading || error}
                          onChange={(event) => {
                            setAvailabilityZone1(event.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Availability zone 2</FormLabel>
                        <Input
                          value={availabilityZone2}
                          disabled={loading || error}
                          onChange={(event) => {
                            setAvailabilityZone2(event.target.value);
                          }}
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid xs={12} sm={cognitoEnabled ? 12 : 6}>
                  <FormControl>
                    <FormLabel>Cognito authentication</FormLabel>
                    <Typography
                      component="label"
                      startDecorator={
                        <Switch
                          checked={cognitoEnabled}
                          onChange={(event) =>
                            setCognitoEnabled(event.target.checked)
                          }
                        />
                      }
                    >
                      {cognitoEnabled ? "Enabled" : "Disabled"}
                    </Typography>
                    <FormHelperText>
                      Use IAM or JWT authentication on your backends and/or
                      Cognito for authenticating users of frontend applications.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                {cognitoEnabled && (
                  <>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Only admin can create users</FormLabel>
                        <Typography
                          component="label"
                          startDecorator={
                            <Switch
                              checked={cognitoAllowAdminCreateUserOnly}
                              onChange={(event) =>
                                setCognitoAllowAdminCreateUserOnly(
                                  event.target.checked
                                )
                              }
                            />
                          }
                        >
                          {cognitoAllowAdminCreateUserOnly
                            ? "Enabled"
                            : "Disabled"}
                        </Typography>
                        <FormHelperText>
                          No self-service user creation.
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6}>
                      <FormControl>
                        <FormLabel>Authentication notifications</FormLabel>
                        <Typography
                          component="label"
                          startDecorator={
                            <Switch
                              checked={cognitoAlertsEnabled}
                              onChange={(event) =>
                                setCognitoAlertsEnabled(event.target.checked)
                              }
                            />
                          }
                        >
                          {cognitoAlertsEnabled ? "Enabled" : "Disabled"}
                        </Typography>
                        <FormHelperText>
                          Get an alert when a user is created.
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>Cognito MFA Required</FormLabel>
                        <Switch
                          checked={cognitoMfaRequired}
                          onChange={(event) =>
                            setCognitoMfaRequired(event.target.checked)
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>Google Client ID</FormLabel>
                        <Input
                          value={googleClientId}
                          disabled={loading || error}
                          onChange={(event) =>
                            setGoogleClientId(event.target.value)
                          }
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>Google Client Secret</FormLabel>
                        <Input
                          value={googleClientSecret}
                          disabled={loading || error}
                          onChange={(event) =>
                            setGoogleClientSecret(event.target.value)
                          }
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Log retention days</FormLabel>
                    <Input
                      type="number"
                      slotProps={{
                        input: {
                          ref: logRetentionDaysInputRef,
                          min: 0,
                          max: 365,
                          step: 1,
                        },
                      }}
                      value={logRetentionDays}
                      disabled={loading || error}
                      error={!validLogRetentionDays}
                      endDecorator={<Typography fontSize="xs">days</Typography>}
                      onChange={(event) => {
                        setLogRetentionDays(Number(event.target.value));
                      }}
                    />
                    <FormHelperText>
                      Allowed values: {LogRetentionDays.join(", ")}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Default Alarms Enabled</FormLabel>
                    <Switch
                      checked={defaultAlarmsEnabled}
                      onChange={(event) =>
                        setDefaultAlarmsEnabled(event.target.checked)
                      }
                    />
                    <FormHelperText>
                      When adding new services, services will be configured with
                      this alarm setting. Enabling alarms sends email or slack
                      notifications about low resource avaibility and other
                      issues.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Default Database Retention (days)</FormLabel>
                    <Input
                      type="number"
                      value={defaultDatabaseRetention}
                      disabled={loading || error}
                      onChange={(event) =>
                        setDefaultDatabaseRetention(Number(event.target.value))
                      }
                    />
                    <FormHelperText>
                      When adding new databases, the retention period will be
                      set to this value.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid xs={12}>
                  <FormControl>
                    <FormLabel>Require Manual CICD Approval</FormLabel>
                    <Switch
                      checked={defaultRequireManualCicdApproval}
                      onChange={(event) =>
                        setDefaultRequireManualCicdApproval(
                          event.target.checked
                        )
                      }
                    />
                    <FormHelperText>
                      If enabled, the services' CICD pipelines will require
                      manual approval before deploying changes.
                    </FormHelperText>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>From Email Name</FormLabel>
                    <Input
                      value={fromEmailName}
                      disabled={loading || error}
                      onChange={(event) => {
                        setFromEmailName(event.target.value);
                      }}
                    />
                    <FormHelperText>
                      The name for the FROM email address.
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </>
            )}
          </>
        )}

        <Grid xs={12}>
          {envFeaturesStatus?.includes("IN_PROGRESS") ? (
            <Typography level="body-lg" sx={{ my: 2 }}>
              Environment is being deployed:{" "}
              <Typography
                variant="soft"
                color="warning"
                fontSize="xs"
                sx={{ borderRadius: "12px", mx: "4px", px: "8px" }}
              >
                {envFeaturesStatus}
              </Typography>
            </Typography>
          ) : (
            <Button
              disabled={
                loading || error || envFeaturesStatus?.includes("IN_PROGRESS")
              }
              onClick={() =>
                updateEnvFeaturesStack(
                  deploymentId,
                  emailNotificationList,
                  deploymentDomainName,
                  logRetentionDays,
                  restApiV1Enabled,
                  restApiV1Subdomain,
                  httpApiV2Enabled,
                  httpApiV2Subdomain,
                  vpcEnabled,
                  vpcCidr,
                  primaryPrivateCidr,
                  secondaryPrivateCidr,
                  primaryPublicCidr,
                  secondaryPublicCidr,
                  availabilityZone1,
                  availabilityZone2,
                  cognitoEnabled,
                  cognitoAllowAdminCreateUserOnly,
                  cognitoMfaRequired,
                  cognitoAlertsEnabled,
                  googleClientId,
                  googleClientSecret,
                  defaultAlarmsEnabled,
                  defaultDatabaseRetention,
                  defaultRequireManualCicdApproval,
                  fromEmailName,
                  null
                )
              }
              sx={{ mt: 2 }}
            >
              {envFeaturesStatus ? "Update" : "Create"} Environment
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};
